import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import axios from "axios";
import { axiosClient } from "@/lib/axiosClient";
import {
    Search, Eye, CheckCircle2, XCircle, Clock,
    Loader2, FileText, User, BookOpen,
    X, Layers, RefreshCw, FileSearch,
    ExternalLink, Wallet, CheckSquare, AlertTriangle, HelpCircle,
    Info, Download, ChevronRight, ChevronDown, Users, Mail, Hash, Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { id as localeId } from "date-fns/locale";

// --- INTERFACES ---
interface SummaryData {
    total_data: number;
    total_paid: number;
    total_unpaid: number;
    total_ready_to_pay: number;
    total_unregistered: number;
}

interface OjsSubmission {
    id: number;
    conference_id: number;
    ojs_submission_id: number;
    current_publication_id?: number | null;
    title: string;
    abstract?: string | null;
    authors: string;
    authors_affiliations?: string | string[] | null; 
    authors_emails?: string | string[] | null;       
    keywords?: string | string[] | null;             
    primary_contact_email?: string | null;
    stage_id: number;
    is_stage_review_active: number;
    stage_review_status_id: number | null;
    status: number;
    status_label: string;
    date_submitted: string;
    date_last_activity: string;
    url_workflow: string;
    created_at: string;
    updated_at: string;
    conference_name: string;
    conference_slug: string;
    payment_status: string | null;
    invoice_number: string | null;
    customer_number: string | null;
    amount: string | null;
    is_ready_to_pay: number;
    raw_data?: string | Record<string, unknown> | null; 
}

interface PaginatedData {
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
    from: number | null;
    to: number | null;
    data: OjsSubmission[];
}

interface OjsAPIResponse {
    status: string;
    summary: SummaryData;
    list: PaginatedData;
}

interface ConferenceOption {
    conference_id: number;
    name: string;
    slug: string;
}

interface OjsDetailResponse {
    status: string;
    data: OjsSubmission & {
        payment: Record<string, unknown> | null;
        conference: ConferenceOption | null;
    };
}

// Error Handler
const parseLaravelError = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data;
        if (data?.message) return data.message;
        return "Terjadi kesalahan sistem. Silakan coba lagi.";
    }
    if (error instanceof Error) return error.message;
    return "Terjadi kesalahan yang tidak diketahui.";
};

// Helper JSON parser
const parseArrayString = (input: string | string[] | null | undefined): string[] => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    try {
        const parsed = JSON.parse(input);
        return Array.isArray(parsed) ? parsed : [input];
    } catch {
        return [input];
    }
};

export default function ConferenceCheckPage() {
    const queryClient = useQueryClient();

    // --- STATE MANAGEMENT ---
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    
    // Filters Utama
    const [conferenceFilter, setConferenceFilter] = useState<string>("all");
    const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>("all");
    const [isReadyToPayFilter, setIsReadyToPayFilter] = useState<string>("all");
    const [hasAbstractFilter, setHasAbstractFilter] = useState<string>("all"); // ✅ Filter Baru
    const [startDate, setStartDate] = useState<string>(""); // ✅ Filter Baru
    const [endDate, setEndDate] = useState<string>(""); // ✅ Filter Baru

    // State Modal & Expand
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [isSyncModalOpen, setIsSyncModalOpen] = useState<boolean>(false);
    const [syncConferenceSlug, setSyncConferenceSlug] = useState<string>("");

    // State Modal Export & Filternya
    const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [exportConferenceId, setExportConferenceId] = useState<string>("all");
    const [exportPaymentStatus, setExportPaymentStatus] = useState<string>("all"); // ✅ Export Filter Baru
    const [exportIsReadyToPay, setExportIsReadyToPay] = useState<string>("all"); // ✅ Export Filter Baru
    const [exportHasAbstract, setExportHasAbstract] = useState<string>("all"); // ✅ Export Filter Baru
    const [exportStartDate, setExportStartDate] = useState<string>("");
    const [exportEndDate, setExportEndDate] = useState<string>("");

    // --- FETCH CONFERENCES ---
    const { data: conferencesData, isLoading: loadingConferences } = useQuery({
        queryKey: ["conferences-dropdown"],
        queryFn: async (): Promise<ConferenceOption[]> => {
            const response = await axiosClient.get(`/conference-billing/conferences`);
            return response.data.data;
        }
    });

    // --- FETCH DATA LIST OJS ---
    const { data: apiResponse, isLoading: listLoading, isFetching } = useQuery({
        // Dependency array ditambah
        queryKey: ["ojs-submissions", page, perPage, conferenceFilter, paymentStatusFilter, isReadyToPayFilter, hasAbstractFilter, startDate, endDate, searchQuery],
        queryFn: async (): Promise<OjsAPIResponse> => {
            let url = `/private/conference-billing/ojs/submissions?page=${page}&limit=${perPage}`;
            if (conferenceFilter !== "all") url += `&conference_id=${conferenceFilter}`;
            if (paymentStatusFilter !== "all") url += `&payment_status=${paymentStatusFilter}`;
            if (isReadyToPayFilter !== "all") url += `&is_ready_to_pay=${isReadyToPayFilter}`;
            if (hasAbstractFilter !== "all") url += `&has_abstract=${hasAbstractFilter}`; // ✅ Params API Baru
            if (startDate) url += `&start_date=${startDate}`; // ✅ Params API Baru
            if (endDate) url += `&end_date=${endDate}`; // ✅ Params API Baru
            if (searchQuery) url += `&search=${searchQuery}`;

            const response = await axiosClient.get(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    const listData = apiResponse?.list;
    const summary = apiResponse?.summary;

    // --- FETCH DATA DETAIL ---
    const { data: detailData, isLoading: detailLoading } = useQuery({
        queryKey: ["ojs-detail", selectedId],
        queryFn: async (): Promise<OjsDetailResponse['data'] | null> => {
            if (!selectedId) return null;
            const response = await axiosClient.get(`/private/conference-billing/ojs/submissions/${selectedId}`);
            return response.data.data;
        },
        enabled: !!selectedId,
    });

    // --- MUTASI SYNC OJS ---
    const syncMutation = useMutation({
        mutationFn: async (slug: string) => {
            const response = await axiosClient.post(`/private/conference-billing/ojs/${slug}/sync`);
            return response.data;
        },
        onSuccess: (data) => {
            toast.success(data.message || "Sinkronisasi berhasil.");
            queryClient.invalidateQueries({ queryKey: ["ojs-submissions"] });
            setIsSyncModalOpen(false);
            setSyncConferenceSlug("");
        },
        onError: (error) => toast.error(parseLaravelError(error)),
    });

    const handleExecuteSync = () => {
        if (!syncConferenceSlug) {
            toast.error("Harap pilih konferensi yang ingin disinkronisasi.");
            return;
        }
        syncMutation.mutate(syncConferenceSlug);
    };

    // ✅ Fungsi Handler Modal Export (Disesuaikan dengan Params API Baru)
    const handleExportExcel = async () => {
        setIsExporting(true);
        try {
            const params = new URLSearchParams();
            if (exportConferenceId && exportConferenceId !== "all") params.append("conference_id", exportConferenceId);
            if (exportPaymentStatus !== "all") params.append("payment_status", exportPaymentStatus);
            if (exportIsReadyToPay !== "all") params.append("is_ready_to_pay", exportIsReadyToPay);
            if (exportHasAbstract !== "all") params.append("has_abstract", exportHasAbstract);
            if (exportStartDate) params.append("start_date", exportStartDate);
            if (exportEndDate) params.append("end_date", exportEndDate);

            const response = await axiosClient.get(`/private/conference-billing/ojs/submissions-export?${params.toString()}`, {
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            let fileName = 'OJS_Submissions_Export.xlsx';
            const disposition = response.headers['content-disposition'];
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                    fileName = matches[1].replace(/['"]/g, '');
                }
            }

            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success("Berhasil mengekspor data ke Excel.");
            setIsExportModalOpen(false);
        } catch (error) {
            console.error("Export error:", error);
            toast.error("Gagal mengekspor data. Silakan coba lagi.");
        } finally {
            setIsExporting(false);
        }
    };

    const toggleRow = (id: number) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            return newSet;
        });
    };

    const formatDateObj = (dateString: string | null) => {
        if (!dateString) return "-";
        try {
            return format(new Date(dateString), "dd MMM yyyy, HH:mm", { locale: localeId });
        } catch {
            return dateString;
        }
    };

    const getPaymentStatusBadge = (status: string | null, isReady: number) => {
        if (status === "paid") return <span className="text-emerald-600 dark:text-emerald-400 text-xs font-bold uppercase flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Lunas</span>;
        if (status === "unpaid") return <span className="text-amber-600 dark:text-amber-400 text-xs font-bold uppercase flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Belum Dibayar</span>;
        if (status === "expired") return <span className="text-rose-600 dark:text-rose-400 text-xs font-bold uppercase flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Expired</span>;
        if (isReady === 1) return <span className="text-blue-600 dark:text-blue-400 text-xs font-bold uppercase flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Siap Bayar (Unregistered)</span>;

        return <span className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase flex items-center gap-1"><HelpCircle className="w-3.5 h-3.5" /> Belum Terdaftar</span>;
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">

            {/* --- HEADER --- */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400">
                            <FileSearch className="w-6 h-6" />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Cek Naskah OJS</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Pantau status naskah dari Open Journal Systems (OJS) dan sinkronisasikan data author untuk kebutuhan tagihan.
                    </p>
                </div>

                <div className="flex items-center gap-3 shrink-0 flex-wrap">
                    <Button
                        variant="outline"
                        onClick={() => {
                            // ✅ Auto-fill filter export berdasarkan filter tabel saat ini
                            setExportConferenceId(conferenceFilter); 
                            setExportPaymentStatus(paymentStatusFilter);
                            setExportIsReadyToPay(isReadyToPayFilter);
                            setExportHasAbstract(hasAbstractFilter);
                            setExportStartDate(startDate);
                            setExportEndDate(endDate);
                            setIsExportModalOpen(true);
                        }}
                        disabled={loadingConferences}
                        className="rounded-xl h-11 px-6 font-bold shadow-sm transition-all border-indigo-200 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-400 dark:hover:bg-indigo-900/30"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        Export Excel
                    </Button>

                    <Button
                        onClick={() => {
                            setSyncConferenceSlug(conferenceFilter !== "all" ? conferenceFilter : "");
                            setIsSyncModalOpen(true);
                        }}
                        disabled={loadingConferences}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-11 px-6 font-bold shadow-md transition-all"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Sync Data OJS
                    </Button>
                </div>
            </div>

            {/* --- SUMMARY CARDS --- */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {isFetching && !summary ? (
                    Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 flex flex-col gap-3 shadow-sm h-[90px]">
                            <Skeleton className="h-4 w-16 dark:bg-slate-800" />
                            <Skeleton className="h-6 w-12 dark:bg-slate-800 mt-auto" />
                        </div>
                    ))
                ) : summary ? (
                    <>
                        <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[90px]">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Total Naskah</p>
                            <p className="text-2xl font-black text-slate-900 dark:text-white">{summary.total_data}</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[90px]">
                            <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest flex items-center gap-1.5"><CheckSquare className="w-3.5 h-3.5" /> Sudah Bayar</p>
                            <p className="text-2xl font-black text-slate-900 dark:text-white">{summary.total_paid}</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[90px]">
                            <p className="text-[10px] font-bold text-amber-500 uppercase tracking-widest flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Belum Bayar</p>
                            <p className="text-2xl font-black text-slate-900 dark:text-white">{summary.total_unpaid}</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[90px]">
                            <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Ready to Pay</p>
                            <p className="text-2xl font-black text-slate-900 dark:text-white">{summary.total_ready_to_pay}</p>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col justify-between h-[90px]">
                            <p className="text-[10px] font-bold text-rose-500 uppercase tracking-widest flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5" /> Unregistered</p>
                            <p className="text-2xl font-black text-slate-900 dark:text-white">{summary.total_unregistered}</p>
                        </div>
                    </>
                ) : null}
            </div>

            {/* --- TOOLBAR (FILTER & SEARCH) --- */}
            <div className="flex flex-col gap-4 w-full">

                {/* ✅ Filter Row 1 (Search, Date, Conf, Ready) */}
                <div className="flex flex-wrap items-center gap-3 w-full">
                    {/* Search Bar */}
                    <div className="relative flex-1 min-w-[250px]">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Cari judul naskah, author, ID OJS..."
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                            className="w-full h-11 pl-11 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm transition-all font-medium"
                        />
                    </div>

                    {/* Filter Tanggal */}
                    <div className="flex items-center gap-2 bg-white dark:bg-slate-900 px-3 py-1.5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800">
                        <Calendar className="w-4 h-4 text-slate-400 hidden sm:block ml-1" />
                        <input type="date" value={startDate} onChange={(e) => {setStartDate(e.target.value); setPage(1);}} className="bg-transparent text-sm font-bold text-slate-700 dark:text-slate-300 w-full sm:w-[130px] outline-none cursor-pointer" />
                        <span className="text-slate-400 font-bold">-</span>
                        <input type="date" value={endDate} onChange={(e) => {setEndDate(e.target.value); setPage(1);}} className="bg-transparent text-sm font-bold text-slate-700 dark:text-slate-300 w-full sm:w-[130px] outline-none cursor-pointer" />
                    </div>

                    <div className="bg-white dark:bg-slate-900 px-4 py-2.5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 flex items-center gap-2 min-w-[170px]">
                        <Layers className="w-4 h-4 text-slate-400 shrink-0" />
                        <select value={conferenceFilter} onChange={(e) => { setConferenceFilter(e.target.value); setPage(1); }} className="w-full bg-transparent text-sm font-bold outline-none cursor-pointer truncate">
                            <option value="all">Semua Conf</option>
                            {conferencesData?.map(conf => <option key={conf.conference_id} value={conf.conference_id.toString()}>{conf.slug.toUpperCase()}</option>)}
                        </select>
                    </div>
                </div>

                {/* ✅ Filter Row 2 (Payment, Abstract, dll) */}
                <div className="flex flex-wrap items-center gap-3 w-full">
                    <div className="bg-white dark:bg-slate-900 px-4 py-2.5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 flex items-center gap-2 flex-1 sm:flex-none min-w-[170px]">
                        <Wallet className="w-4 h-4 text-slate-400 shrink-0" />
                        <select value={paymentStatusFilter} onChange={(e) => { setPaymentStatusFilter(e.target.value); setPage(1); }} className="w-full bg-transparent text-sm font-bold outline-none cursor-pointer truncate">
                            <option value="all">Status: Semua</option>
                            <option value="paid">Lunas</option>
                            <option value="unpaid">Belum Bayar</option>
                            <option value="unregistered">Belum Daftar</option>
                        </select>
                    </div>

                    <div className="bg-white dark:bg-slate-900 px-4 py-2.5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 flex items-center gap-2 flex-1 sm:flex-none min-w-[170px]">
                        <CheckSquare className="w-4 h-4 text-slate-400 shrink-0" />
                        <select value={isReadyToPayFilter} onChange={(e) => { setIsReadyToPayFilter(e.target.value); setPage(1); }} className="w-full bg-transparent text-sm font-bold outline-none cursor-pointer truncate">
                            <option value="all">Kesiapan: Semua</option>
                            <option value="1">Siap Bayar</option>
                            <option value="0">Belum Siap</option>
                        </select>
                    </div>

                    <div className="bg-white dark:bg-slate-900 px-4 py-2.5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 flex items-center gap-2 flex-1 sm:flex-none min-w-[170px]">
                        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                        <select value={hasAbstractFilter} onChange={(e) => { setHasAbstractFilter(e.target.value); setPage(1); }} className="w-full bg-transparent text-sm font-bold outline-none cursor-pointer truncate">
                            <option value="all">Abstrak: Semua</option>
                            <option value="1">Ada Abstrak</option>
                            <option value="0">Tanpa Abstrak</option>
                        </select>
                    </div>
                </div>

            </div>

            {/* --- DATA TABLE --- */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">

                {isFetching && !listLoading && (
                    <div className="absolute inset-0 z-10 bg-white/50 dark:bg-slate-900/50 backdrop-blur-[1px] flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                    </div>
                )}

                <div className="overflow-x-auto pb-4">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                            <tr>
                                <th className="px-4 py-5 border-b border-slate-100 dark:border-slate-800 w-10 text-center"></th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">OJS ID / Tgl Submit</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Judul & Author</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Conference</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Status Pembayaran</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-right sticky right-0 z-10 bg-slate-50 dark:bg-slate-800/90 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)]">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {listLoading ? (
                                <tr>
                                    <td colSpan={6} className="p-12 text-center text-slate-500"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-indigo-500" /> Memuat data naskah...</td>
                                </tr>
                            ) : (!listData || listData.data.length === 0) ? (
                                <tr>
                                    <td colSpan={6} className="p-12 text-center text-slate-500">Tidak ada naskah yang ditemukan.</td>
                                </tr>
                            ) : (
                                listData.data.map((item) => (
                                    <React.Fragment key={item.id}>
                                        <tr className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors bg-white dark:bg-slate-900">
                                            <td className="px-4 py-4 text-center cursor-pointer" onClick={() => toggleRow(item.id)}>
                                                <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors text-slate-400">
                                                    {expandedRows.has(item.id) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="font-black text-indigo-600 dark:text-indigo-400 font-mono text-base">#{item.ojs_submission_id}</p>
                                                <p className="text-[11px] text-slate-500 mt-1">{formatDateObj(item.date_submitted)}</p>
                                            </td>
                                            <td className="px-6 py-4 whitespace-normal min-w-[300px] max-w-[400px]">
                                                <p className="font-bold text-slate-900 dark:text-white text-sm line-clamp-2 leading-snug" title={item.title}>{item.title}</p>
                                                <p className="text-[11px] font-medium text-slate-500 mt-1.5 flex items-center gap-1.5"><User className="w-3 h-3" /> {item.authors}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="font-bold text-slate-700 dark:text-slate-300 uppercase text-[11px] px-2.5 py-1 bg-slate-100 dark:bg-slate-800 rounded-md">
                                                    {item.conference_slug}
                                                </span>
                                                <p className="text-[10px] text-slate-500 mt-1.5 font-bold uppercase tracking-wider">{item.status_label}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-1.5">
                                                    {getPaymentStatusBadge(item.payment_status, item.is_ready_to_pay)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right sticky right-0 z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50/50 dark:group-hover:bg-slate-800/80 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)] transition-colors">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setSelectedId(item.id)}
                                                    className="rounded-xl text-indigo-600 border-indigo-200 hover:bg-indigo-50 dark:text-indigo-400 dark:border-indigo-900/50 dark:hover:bg-indigo-900/30"
                                                >
                                                    <Eye className="w-4 h-4 mr-2" /> Detail
                                                </Button>
                                            </td>
                                        </tr>

                                        {expandedRows.has(item.id) && (
                                            <tr className="bg-slate-50/80 dark:bg-slate-800/30 border-b border-slate-100 dark:border-slate-800">
                                                <td colSpan={6} className="px-8 py-5 shadow-inner">
                                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-in fade-in slide-in-from-top-2 duration-300">

                                                        {/* Kolom 1: Author & Kontak */}
                                                        <div className="space-y-4">
                                                            <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Data Penulis</h5>
                                                            <div>
                                                                <p className="text-xs text-slate-500 mb-1">Daftar Afiliasi</p>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {parseArrayString(item.authors_affiliations).length > 0 ? (
                                                                        parseArrayString(item.authors_affiliations).map((affil, idx) => (
                                                                            <span key={idx} className="text-[10px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2 py-0.5 rounded-md text-slate-600 dark:text-slate-300">{affil}</span>
                                                                        ))
                                                                    ) : (
                                                                        <span className="text-xs text-slate-400">-</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-slate-500 mb-1">Email Penulis</p>
                                                                <div className="flex flex-col gap-1 text-xs">
                                                                    {parseArrayString(item.authors_emails).length > 0 ? (
                                                                        parseArrayString(item.authors_emails).map((email, idx) => (
                                                                            <span key={idx} className="flex items-center gap-1 text-slate-700 dark:text-slate-300"><Mail className="w-3 h-3 text-slate-400" /> {email}</span>
                                                                        ))
                                                                    ) : (
                                                                        <span className="text-xs text-slate-400">-</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Kolom 2: Metadata Naskah */}
                                                        <div className="space-y-4">
                                                            <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Hash className="w-3.5 h-3.5" /> Metadata OJS</h5>
                                                            <div className="grid grid-cols-2 gap-4">
                                                                <div>
                                                                    <p className="text-xs text-slate-500 mb-0.5">Primary Contact</p>
                                                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate" title={item.primary_contact_email || ""}>{item.primary_contact_email || "-"}</p>
                                                                </div>
                                                                <div>
                                                                    <p className="text-xs text-slate-500 mb-0.5">Publication ID</p>
                                                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{item.current_publication_id || "-"}</p>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-slate-500 mb-1.5">Kata Kunci (Keywords)</p>
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {parseArrayString(item.keywords).length > 0 ? (
                                                                        parseArrayString(item.keywords).map((kw, idx) => (
                                                                            <span key={idx} className="text-[10px] bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800 px-2 py-0.5 rounded-md font-medium uppercase tracking-wider">{kw}</span>
                                                                        ))
                                                                    ) : (
                                                                        <span className="text-xs text-slate-400">-</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Kolom 3: Abstrak */}
                                                        <div className="space-y-3">
                                                            <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Abstrak</h5>
                                                            {item.abstract ? (
                                                                <div
                                                                    className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed bg-white dark:bg-slate-900/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700 max-h-[160px] overflow-y-auto prose prose-sm prose-slate dark:prose-invert"
                                                                    dangerouslySetInnerHTML={{ __html: item.abstract }}
                                                                />
                                                            ) : (
                                                                <div className="p-4 rounded-xl border border-slate-200 border-dashed dark:border-slate-700 flex items-center justify-center h-20 opacity-50">
                                                                    <span className="text-xs italic text-slate-500">Abstrak tidak tersedia di sistem.</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {listData && listData.total > 0 && (
                    <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-3 text-slate-500 font-medium">
                            <span>Menampilkan {listData.from || 0} - {listData.to || 0} dari {listData.total} data</span>
                            <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 hidden sm:block"></div>
                            <div className="flex items-center gap-2 sm:flex">
                                <label htmlFor="perPage">Tampilkan:</label>
                                <select
                                    id="perPage"
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" className="rounded-lg" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Sebelumnya</Button>
                            <Button variant="outline" size="sm" className="rounded-lg" disabled={page === listData.last_page} onClick={() => setPage(p => p + 1)}>Selanjutnya</Button>
                        </div>
                    </div>
                )}
            </div>

            {/* ================================================================================= */}
            {/* ✅ MODAL EXPORT DATA OJS */}
            {/* ================================================================================= */}
            {isExportModalOpen && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 md:p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] w-full max-w-2xl flex flex-col shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in zoom-in-95">
                        <div className="px-8 py-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 shrink-0">
                            <div>
                                <h3 className="font-black text-xl text-slate-900 dark:text-white flex items-center gap-2">
                                    <Download className="w-6 h-6 text-indigo-600" /> Export Excel
                                </h3>
                                <p className="text-xs text-slate-500 font-medium mt-1">Unduh data naskah OJS dalam format Excel.</p>
                            </div>
                            <button onClick={() => !isExporting && setIsExportModalOpen(false)} disabled={isExporting} className="p-2.5 bg-slate-200/50 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-full transition-colors disabled:opacity-50">
                                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                            </button>
                        </div>

                        <div className="p-6 md:p-8 space-y-6">
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* Pilihan Conference */}
                                <div className="space-y-3 col-span-1 sm:col-span-2">
                                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Pilih Conference (Opsional)</label>
                                    <select
                                        value={exportConferenceId}
                                        onChange={(e) => setExportConferenceId(e.target.value)}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                    >
                                        <option value="all">Semua Conference</option>
                                        {conferencesData?.map(conf => (
                                            <option key={conf.conference_id} value={conf.conference_id.toString()}>
                                                {conf.slug.toUpperCase()} - {conf.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Filter Payment Status */}
                                <div className="space-y-3">
                                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Status Pembayaran</label>
                                    <select
                                        value={exportPaymentStatus}
                                        onChange={(e) => setExportPaymentStatus(e.target.value)}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                    >
                                        <option value="all">Semua Status</option>
                                        <option value="paid">Lunas</option>
                                        <option value="unpaid">Belum Bayar</option>
                                        <option value="unregistered">Belum Daftar</option>
                                    </select>
                                </div>

                                {/* Filter Is Ready to Pay */}
                                <div className="space-y-3">
                                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Kesiapan Bayar</label>
                                    <select
                                        value={exportIsReadyToPay}
                                        onChange={(e) => setExportIsReadyToPay(e.target.value)}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                    >
                                        <option value="all">Semua Kesiapan</option>
                                        <option value="1">Siap Bayar</option>
                                        <option value="0">Belum Siap</option>
                                    </select>
                                </div>

                                {/* Filter Has Abstract */}
                                <div className="space-y-3 col-span-1 sm:col-span-2">
                                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Kelengkapan Abstrak</label>
                                    <select
                                        value={exportHasAbstract}
                                        onChange={(e) => setExportHasAbstract(e.target.value)}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                    >
                                        <option value="all">Semua Naskah</option>
                                        <option value="1">Memiliki Abstrak</option>
                                        <option value="0">Tanpa Abstrak</option>
                                    </select>
                                </div>

                                {/* Filter Rentang Waktu (Opsional) */}
                                <div className="space-y-3">
                                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Tanggal Mulai</label>
                                    <input
                                        type="date"
                                        value={exportStartDate}
                                        onChange={(e) => setExportStartDate(e.target.value)}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Tanggal Akhir</label>
                                    <input
                                        type="date"
                                        value={exportEndDate}
                                        onChange={(e) => setExportEndDate(e.target.value)}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                    />
                                </div>
                            </div>

                        </div>

                        <div className="px-6 py-4 md:px-8 md:py-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 shrink-0">
                            <Button variant="ghost" onClick={() => setIsExportModalOpen(false)} disabled={isExporting} className="rounded-xl h-11 px-6">Batal</Button>
                            <Button
                                onClick={handleExportExcel}
                                disabled={isExporting}
                                className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11 px-6 shadow-sm transition-all"
                            >
                                {isExporting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Download className="w-5 h-5 mr-2" />}
                                {isExporting ? "Mengunduh..." : "Export Excel"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* ================================================================================= */}
            {/* ✅ MODAL SYNC DATA OJS (Ditambahkan z-[999] agar berada di atas Navbar) */}
            {/* ================================================================================= */}
            {isSyncModalOpen && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 md:p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] w-full max-w-lg flex flex-col shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in zoom-in-95">
                        <div className="px-8 py-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 shrink-0">
                            <div>
                                <h3 className="font-black text-xl text-slate-900 dark:text-white flex items-center gap-2">
                                    <RefreshCw className="w-6 h-6 text-indigo-600" /> Sinkronisasi OJS
                                </h3>
                                <p className="text-xs text-slate-500 font-medium mt-1">Tarik data naskah terbaru secara realtime.</p>
                            </div>
                            <button onClick={() => setIsSyncModalOpen(false)} className="p-2.5 bg-slate-200/50 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-full transition-colors">
                                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                            </button>
                        </div>

                        <div className="p-6 md:p-8 space-y-6">
                            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800/50 p-4 rounded-2xl flex gap-3 shadow-sm">
                                <Info className="w-6 h-6 text-indigo-600 dark:text-indigo-400 shrink-0" />
                                <p className="text-sm text-indigo-900 dark:text-indigo-200 leading-relaxed">
                                    Sistem akan melakukan penarikan data <i>submission</i> terbaru secara langsung dari server portal <strong>https://eproceeding.undiksha.ac.id/</strong>.
                                </p>
                            </div>

                            <div className="space-y-3">
                                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Pilih Conference Track</label>
                                <select
                                    value={syncConferenceSlug}
                                    onChange={(e) => setSyncConferenceSlug(e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm font-medium focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                >
                                    <option value="" disabled>-- Pilih Conference --</option>
                                    {conferencesData?.map(conf => (
                                        <option key={conf.conference_id} value={conf.slug}>
                                            {conf.slug.toUpperCase()} - {conf.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="px-6 py-4 md:px-8 md:py-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 shrink-0">
                            <Button variant="ghost" onClick={() => setIsSyncModalOpen(false)} className="rounded-xl h-11 px-6">Batal</Button>
                            <Button
                                onClick={handleExecuteSync}
                                disabled={!syncConferenceSlug || syncMutation.isPending}
                                className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold h-11 px-6 shadow-sm transition-all"
                            >
                                {syncMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <RefreshCw className="w-5 h-5 mr-2" />}
                                {syncMutation.isPending ? "Sedang Sinkronisasi..." : "Mulai Sync"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* ================================================================================= */}
            {/* ✅ MODAL DETAIL OJS (Ditambahkan z-[999] agar berada di atas Navbar) */}
            {/* ================================================================================= */}
            {selectedId && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 md:p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] w-full max-w-4xl max-h-[95vh] flex flex-col shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in zoom-in-95">

                        {/* Modal Header */}
                        <div className="px-8 py-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 shrink-0">
                            <div>
                                <h3 className="font-black text-xl text-slate-900 dark:text-white flex items-center gap-2">
                                    <BookOpen className="w-6 h-6 text-indigo-600" /> Detail Naskah
                                </h3>
                                <p className="text-sm text-slate-500 font-mono mt-1">OJS Submission ID: #{detailData?.ojs_submission_id || "..."}</p>
                            </div>
                            <button onClick={() => setSelectedId(null)} className="p-2.5 bg-slate-200/50 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-full transition-colors">
                                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto p-6 md:p-8">
                            {detailLoading || !detailData ? (
                                <div className="py-20 flex flex-col items-center text-slate-400">
                                    <Loader2 className="w-10 h-10 animate-spin mb-4 text-indigo-600" />
                                    <p>Mengambil detail naskah...</p>
                                </div>
                            ) : (
                                <div className="space-y-8">
                                    {/* Info Utama */}
                                    <div className="bg-indigo-50 dark:bg-indigo-900/10 p-6 rounded-2xl border border-indigo-100 dark:border-indigo-900/30 shadow-sm">
                                        <div className="flex justify-between items-start gap-4 mb-4">
                                            <span className="font-bold text-indigo-700 dark:text-indigo-400 uppercase text-[10px] tracking-widest px-2.5 py-1 bg-indigo-100 dark:bg-indigo-900/50 rounded-md">
                                                {detailData.conference?.slug || "UNKNOWN"}
                                            </span>
                                            {getPaymentStatusBadge(detailData.payment_status, detailData.is_ready_to_pay)}
                                        </div>
                                        <h2 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white leading-snug mb-3">
                                            {detailData.title}
                                        </h2>
                                        <p className="text-sm font-medium text-slate-600 dark:text-slate-300 flex items-center gap-2">
                                            <User className="w-4 h-4 text-indigo-500" /> {detailData.authors}
                                        </p>
                                    </div>

                                    {/* Grid Statistik OJS */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Status OJS</p>
                                            <p className="font-bold text-slate-900 dark:text-white">{detailData.status_label}</p>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Stage ID</p>
                                            <p className="font-bold text-slate-900 dark:text-white">{detailData.stage_id}</p>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Date Submitted</p>
                                            <p className="font-bold text-slate-900 dark:text-white text-sm">{formatDateObj(detailData.date_submitted)}</p>
                                        </div>
                                        <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Last Activity</p>
                                            <p className="font-bold text-slate-900 dark:text-white text-sm">{formatDateObj(detailData.date_last_activity)}</p>
                                        </div>
                                    </div>

                                    {/* Tombol OJS Workflow */}
                                    {detailData.url_workflow && (
                                        <div className="flex justify-end">
                                            <a
                                                href={detailData.url_workflow}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-200 text-white dark:text-slate-900 px-6 py-3 rounded-xl font-bold text-sm transition-colors shadow-md"
                                            >
                                                Buka Naskah di OJS <ExternalLink className="w-4 h-4" />
                                            </a>
                                        </div>
                                    )}

                                    {/* ✅ FIX TYPESCRIPT: Tangani Parsing JSON dengan aman untuk API baru yang mengirim object langsung */}
                                    {detailData.raw_data && (
                                        <div className="mt-8 space-y-3">
                                            <h4 className="font-bold text-sm text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                                <FileText className="w-4 h-4 text-slate-400" /> Raw Response OJS API
                                            </h4>
                                            <div className="bg-[#0D1117] rounded-xl p-4 overflow-auto max-h-[300px] border border-slate-800">
                                                <pre className="text-[11px] text-green-400 font-mono leading-relaxed whitespace-pre-wrap break-all">
                                                    {(() => {
                                                        try {
                                                            const raw = detailData.raw_data;
                                                            // Jika API sudah me-return data berupa Object, langsung stringify
                                                            if (typeof raw === 'object' && raw !== null) {
                                                                return JSON.stringify(raw, null, 2);
                                                            }
                                                            // Jika API me-return string seperti sebelumnya
                                                            if (typeof raw === 'string') {
                                                                let cleanRaw = raw;
                                                                if (cleanRaw.startsWith('"') && cleanRaw.endsWith('"')) {
                                                                    cleanRaw = cleanRaw.slice(1, -1).replace(/\\"/g, '"').replace(/\\\\/g, '\\');
                                                                }
                                                                const parsed = JSON.parse(cleanRaw);
                                                                return JSON.stringify(parsed, null, 2);
                                                            }
                                                            return String(raw);
                                                        } catch (e: unknown) {
                                                            console.error("JSON parse error:", e);
                                                            // Fallback aman: kembalikan string apa pun bentuknya
                                                            return typeof detailData.raw_data === 'object' 
                                                                ? JSON.stringify(detailData.raw_data, null, 2) 
                                                                : String(detailData.raw_data);
                                                        }
                                                    })()}
                                                </pre>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}